import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Paper,
  Stack,
  Divider
} from '@mui/material';
import {
  Search,
  Lock,
  LocationOn,
  Build,
  People,
  Close,
  SwapHoriz,
  Map as MapIcon,
  ViewModule,
  ViewList,
  VpnKey,
  Group,
  Sync,
  InfoOutlined,
  StickyNote2,
  History
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { dataverseService } from '../services/dataverseService';
import { handleDataverseLoadError, getDataverseErrorMessage } from '../lib/dataverseErrorHandler';
import { loadDataverseWithDemoFallback, DEMO_DATA_BANNER_TEXT } from '../lib/loadDataverseData';
import { ORDER_TYPES } from '../data/orderTypes';
import { isOrdersEnabled } from '../config/features';
import { getCustomerById, getCustomerMapCenter } from '../config/customers';
import { getResolvedCustomerId } from '../config/environment';
import { getCustomerFromCurrentHostname } from '../lib/customerFromHostname';
import { getSelectedCustomer } from '../lib/selectedCustomer';
import LocksMap from '../components/LocksMap';
import KeyRequestDialog from '../components/KeyRequestDialog';
import LockKeysDialog from '../components/LockKeysDialog';
import AddToAccessGroupDialog from '../components/AddToAccessGroupDialog';
import { KEY_REQUEST_SCOPE } from '../config/keyRequest';
import {
  getProviderChipColor,
  listProvidersForFilter,
  countLocksByProvider
} from '../config/lockProviders';

function formatDateTime(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString('no-NO');
}

function getLocationSourceLabel(source) {
  if (source === 'location') {
    return 'Eksakt posisjon';
  }

  if (source === 'locationGuess') {
    return 'Estimert posisjon';
  }

  return source || null;
}

function detailValue(value) {
  return value || 'Ikke oppgitt';
}

function getOrganizationName(lock) {
  return (
    lock?.organizationName ||
    lock?.duplicateSourceLock?.project?.organizationName ||
    null
  );
}

function getStatusColor(assignedCount) {
  if (assignedCount === 0) return 'default';
  if (assignedCount <= 2) return 'success';
  return 'warning';
}

function isInactiveLocation(lock) {
  return String(lock?.location || '')
    .trim()
    .toLowerCase()
    .startsWith('inaktiv');
}

function LockMetaChips({ lock, size = 'small' }) {
  const organizationName = getOrganizationName(lock);
  const hasNote = Boolean(lock.note && String(lock.note).trim());
  const unlockOnly = lock.canBeLocked === false;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {organizationName && (
        <Chip label={organizationName} size={size} color="secondary" variant="outlined" />
      )}
      {unlockOnly && (
        <Chip
          label="Kun åpne"
          size={size}
          variant="outlined"
          color="default"
          title="Låsen kan åpnes, men ikke låses fra appen"
        />
      )}
      {lock.duplicateLock && (
        <Chip
          label="Tvilling"
          size={size}
          sx={{ bgcolor: '#7b1fa2', color: 'common.white' }}
        />
      )}
      {hasNote && (
        <Chip
          icon={<StickyNote2 sx={{ fontSize: 16 }} />}
          label="Notat"
          size={size}
          variant="outlined"
          color="default"
        />
      )}
    </Box>
  );
}

const LockDetailsDialog = ({ open, lock, onClose, onNoteSaved, onViewLog }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  useEffect(() => {
    if (!open || !lock) {
      return undefined;
    }

    setDetails(null);
    setError('');
    setNoteDraft(lock.note || '');
    setNoteError('');
    setNoteSaved(false);
    setImageDialogOpen(false);

    let cancelled = false;

    const load = async () => {
      if (lock.provider !== 'unloc' || !lock.lockId) {
        return;
      }

      setLoading(true);
      try {
        const next = await dataverseService.lockService.getDetails(lock.lockId);
        if (!cancelled) {
          setDetails(next);
        }
      } catch (loadError) {
        console.warn(`Could not load details for ${lock.lockId}:`, loadError);
        if (!cancelled) {
          setError(loadError?.message || 'Kunne ikke hente detaljer');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, lock]);

  if (!lock) {
    return null;
  }

  const detailSource = details
    ? {
        ...lock,
        ...details,
        duplicateSourceLock: details.duplicateSourceLock || lock.duplicateSourceLock,
        organizationName:
          details.duplicateSourceLock?.project?.organizationName ||
          lock.organizationName
      }
    : lock;

  const projectName = detailSource.duplicateSourceLock?.project?.name || null;
  const projectId = detailSource.duplicateSourceLock?.project?.id || null;
  const projectOrganizationId = detailSource.duplicateSourceLock?.project?.organizationId || null;
  const projectOrganization = getOrganizationName(detailSource);
  const sourceLockName = detailSource.duplicateSourceLock?.name || null;
  const sourceLockId = detailSource.duplicateSourceLock?.id || null;
  const locationSourceLabel = getLocationSourceLabel(detailSource.locationSource);
  const unlocUpdatedLabel = formatDateTime(detailSource.unlocUpdatedOn);
  const unlocCreatedLabel = formatDateTime(detailSource.unlocCreatedOn);
  const showUnlocSection =
    lock.provider === 'unloc' ||
    Boolean(
      detailSource.city ||
        detailSource.vendorType ||
        locationSourceLabel ||
        sourceLockName ||
        sourceLockId ||
        projectName ||
        projectId ||
        projectOrganizationId ||
        projectOrganization ||
        unlocUpdatedLabel ||
        unlocCreatedLabel
    );

  const handleSaveNote = async () => {
    setSavingNote(true);
    setNoteError('');
    setNoteSaved(false);
    try {
      const saved = await dataverseService.lockService.updateNotat(lock.lockId, noteDraft);
      setNoteDraft(saved || '');
      setNoteSaved(true);
      onNoteSaved?.(lock.lockId, saved);
    } catch (saveError) {
      console.error('Could not save lock note:', saveError);
      setNoteError(getDataverseErrorMessage(saveError) || 'Kunne ikke lagre notat');
    } finally {
      setSavingNote(false);
    }
  };

  const imageUrl = detailSource.imageUrl || lock.imageUrl || null;

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        <Box>
          <Typography variant="h6">{lock.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            ID: {lock.lockId}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Lukk">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <LockMetaChips lock={{ ...lock, organizationName: projectOrganization, note: noteDraft || lock.note }} />

          {imageUrl ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Bilde
              </Typography>
              <Box
                component="img"
                src={imageUrl}
                alt={`Bilde av ${lock.name}`}
                onClick={() => setImageDialogOpen(true)}
                sx={{
                  width: '100%',
                  maxHeight: 280,
                  objectFit: 'cover',
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    opacity: 0.95
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Klikk for å forstørre
              </Typography>
            </Box>
          ) : null}

          <Box>
            <Typography variant="body2" color="text.secondary">
              Lokasjon
            </Typography>
            <Typography variant="body1">{lock.location}</Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary">
              Leverandør
            </Typography>
            <Typography variant="body1">{lock.providerLabel || lock.provider}</Typography>
          </Box>

          {showUnlocSection && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Unloc-detaljer
              </Typography>
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Laster detaljer…</Typography>
                </Box>
              )}
              {error && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {error}
                </Alert>
              )}
              <Stack spacing={0.5}>
                <Typography variant="body2">By: {detailValue(detailSource.city)}</Typography>
                <Typography variant="body2">Vendor type: {detailValue(detailSource.vendorType)}</Typography>
                <Typography variant="body2">Posisjon: {detailValue(locationSourceLabel)}</Typography>
                <Typography variant="body2">Kildelås ID: {detailValue(sourceLockId)}</Typography>
                <Typography variant="body2">Kildelås navn: {detailValue(sourceLockName)}</Typography>
                <Typography variant="body2">Prosjekt-ID: {detailValue(projectId)}</Typography>
                <Typography variant="body2">Prosjekt: {detailValue(projectName)}</Typography>
                <Typography variant="body2">Organisasjon-ID: {detailValue(projectOrganizationId)}</Typography>
                <Typography variant="body2">Organisasjon: {detailValue(projectOrganization)}</Typography>
                <Typography variant="body2">Unloc oppdatert: {detailValue(unlocUpdatedLabel)}</Typography>
                <Typography variant="body2">Unloc opprettet: {detailValue(unlocCreatedLabel)}</Typography>
              </Stack>
            </Box>
          )}

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Notat
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="Internt notat (synkes ikke til Unloc)"
              value={noteDraft}
              onChange={(event) => {
                setNoteDraft(event.target.value);
                setNoteSaved(false);
              }}
            />
            {noteError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {noteError}
              </Alert>
            )}
            {noteSaved && !noteError && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Notat lagret
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {onViewLog && (
          <Button startIcon={<History />} onClick={() => onViewLog(lock)}>
            Hendelseslogg
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Lukk</Button>
        <Button
          variant="contained"
          onClick={handleSaveNote}
          disabled={savingNote}
          startIcon={savingNote ? <CircularProgress size={16} /> : <StickyNote2 />}
        >
          {savingNote ? 'Lagrer…' : 'Lagre notat'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{lock.name} - Bilde</Typography>
        <IconButton onClick={() => setImageDialogOpen(false)} size="small" aria-label="Lukk bilde">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt={`Stort bilde av ${lock.name}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: 1
            }}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

const LockCard = ({
  lock,
  accessGroups = [],
  onCreateMoveOrder,
  onCreateServiceOrder,
  onManageKeys,
  onAddToGroup,
  onOpenDetails,
  onViewLog
}) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  const handleImageClick = () => {
    setImageDialogOpen(true);
  };

  const handleImageDialogClose = () => {
    setImageDialogOpen(false);
  };

  const getBatteryColor = (batteryLevel) => {
    switch (batteryLevel) {
      case 'High':
        return 'success';
      case 'Medium':
        return 'warning';
      case 'Low':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card className="card-hover fade-in">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
            {lock.imageUrl ? (
              <Box
                component="img"
                src={lock.imageUrl}
                alt={`Bilde av ${lock.name}`}
                onClick={handleImageClick}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: 'success.main',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'scale(1.05)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              />
            ) : (
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <Lock />
              </Avatar>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="div" fontWeight="600">
              {lock.name}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <LockMetaChips lock={lock} />
            </Box>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocationOn sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2">{lock.location}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Chip
              label={lock.providerLabel || lock.provider}
              color={getProviderChipColor(lock.provider)}
              size="small"
            />
            {lock.type &&
              lock.type.toLowerCase() !== (lock.provider || '').toLowerCase() &&
              lock.type.toLowerCase() !== (lock.providerLabel || '').toLowerCase() && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Legacy vendor: {lock.type}
                </Typography>
              )}
          </Grid>
        </Grid>

        {lock.externalId && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Ekstern ID: {lock.externalId}
          </Typography>
        )}

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <People sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">
              {lock.assignedResidents.length} innbygger(e) tilordnet
            </Typography>
          </Box>
          {lock.assignedResidents.length > 0 && (
            <Box sx={{ ml: 2 }}>
              {lock.assignedResidents.map((residentId, index) => (
                <Chip
                  key={index}
                  label={`Innbygger ${residentId.substring(0, 8)}...`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}
        </Box>

        {accessGroups.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Tilgangsgrupper
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {accessGroups.map((group) => (
                <Chip key={group.id} label={group.name} size="small" icon={<Group />} variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Build sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2">
                Opprettet: {new Date(lock.lastMaintenance).toLocaleDateString('no-NO')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            {lock.batteryLevel !== 'NotAvailable' && (
              <Chip
                label={`Batteri: ${lock.batteryLevel}`}
                color={getBatteryColor(lock.batteryLevel)}
                size="small"
              />
            )}
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Chip
            label={lock.assignedResidents.length === 0 ? 'Tilgjengelig' : 'Tilordnet'}
            color={getStatusColor(lock.assignedResidents.length)}
            size="small"
          />
          <Chip label={lock.status} color="success" size="small" variant="outlined" />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<InfoOutlined />}
            onClick={() => onOpenDetails(lock)}
          >
            Detaljer
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VpnKey />}
            onClick={() => onManageKeys(lock)}
          >
            {lock.provider === 'unloc' ? 'Tilgang' : 'Nøkler'}
          </Button>
          {lock.provider === 'unloc' && onAddToGroup && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Group />}
              onClick={() => onAddToGroup(lock)}
            >
              Legg til i gruppe…
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<History />}
            onClick={() => onViewLog(lock)}
          >
            Logg
          </Button>
          {isOrdersEnabled() && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SwapHoriz />}
                onClick={() => onCreateMoveOrder(lock)}
              >
                Flytt lås
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Build />}
                onClick={() => onCreateServiceOrder(lock)}
              >
                Meld service
              </Button>
            </>
          )}
        </Box>
      </CardContent>

      <Dialog open={imageDialogOpen} onClose={handleImageDialogClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{lock.name} - Bilde</Typography>
          <IconButton onClick={handleImageDialogClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {lock.imageUrl && (
            <Box
              component="img"
              src={lock.imageUrl}
              alt={`Stort bilde av ${lock.name}`}
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 1
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const LockRow = ({
  lock,
  accessGroups = [],
  onCreateMoveOrder,
  onCreateServiceOrder,
  onManageKeys,
  onAddToGroup,
  onOpenDetails,
  onViewLog
}) => {
  const assignedCount = lock.assignedResidents?.length || 0;
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  return (
    <>
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}
    >
      {lock.imageUrl ? (
        <Box
          component="img"
          src={lock.imageUrl}
          alt={`Bilde av ${lock.name}`}
          onClick={() => setImageDialogOpen(true)}
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            objectFit: 'cover',
            cursor: 'pointer',
            flexShrink: 0,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              transform: 'scale(1.05)',
              transition: 'all 0.15s ease-in-out'
            }
          }}
        />
      ) : (
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            flexShrink: 0
          }}
        >
          <Lock fontSize="small" />
        </Avatar>
      )}

      <Box sx={{ minWidth: 180, flex: '1 1 180px' }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {lock.name}
        </Typography>
      </Box>

      <Box sx={{ flex: '1 1 140px', minWidth: 120 }}>
        <LockMetaChips lock={lock} />
      </Box>

      <Box sx={{ flex: '1 1 160px', minWidth: 140, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2" noWrap title={lock.location}>
          {lock.location}
        </Typography>
      </Box>

      <Chip
        label={lock.providerLabel || lock.provider}
        color={getProviderChipColor(lock.provider)}
        size="small"
      />

      <Chip
        label={assignedCount === 0 ? 'Tilgjengelig' : 'Tilordnet'}
        color={getStatusColor(assignedCount)}
        size="small"
      />

      {accessGroups.length > 0 && (
        <Chip
          icon={<Group />}
          label={`${accessGroups.length} gruppe(r)`}
          size="small"
          variant="outlined"
        />
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, ml: 'auto' }}>
        <Button size="small" startIcon={<InfoOutlined />} onClick={() => onOpenDetails(lock)}>
          Detaljer
        </Button>
        <Button size="small" startIcon={<VpnKey />} onClick={() => onManageKeys(lock)}>
          {lock.provider === 'unloc' ? 'Tilgang' : 'Nøkler'}
        </Button>
        {lock.provider === 'unloc' && onAddToGroup && (
          <Button size="small" startIcon={<Group />} onClick={() => onAddToGroup(lock)}>
            Gruppe
          </Button>
        )}
        <Button size="small" startIcon={<History />} onClick={() => onViewLog(lock)}>
          Logg
        </Button>
        {isOrdersEnabled() && (
          <>
            <IconButton size="small" aria-label="Flytt lås" onClick={() => onCreateMoveOrder(lock)}>
              <SwapHoriz fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="Meld service" onClick={() => onCreateServiceOrder(lock)}>
              <Build fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
    </Paper>

    <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{lock.name} - Bilde</Typography>
        <IconButton onClick={() => setImageDialogOpen(false)} size="small" aria-label="Lukk bilde">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {lock.imageUrl && (
          <Box
            component="img"
            src={lock.imageUrl}
            alt={`Stort bilde av ${lock.name}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: 1
            }}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

const LocksPage = () => {
  const navigate = useNavigate();
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('dataverse');
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [keyDialogLock, setKeyDialogLock] = useState(null);
  const [groupsByLockId, setGroupsByLockId] = useState({});
  const [addGroupLock, setAddGroupLock] = useState(null);
  const [detailsLock, setDetailsLock] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const mapCenter = useMemo(
    () =>
      getCustomerMapCenter(
        getCustomerFromCurrentHostname() ||
          getSelectedCustomer() ||
          getCustomerById(getResolvedCustomerId())
      ),
    []
  );

  const loadLocks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, source } = await loadDataverseWithDemoFallback(
        () => dataverseService.getLocksWithResidents(),
        async () => {
          const { mockApi } = await import('../data/demoData');
          return mockApi.getLocks();
        }
      );
      setLocks(data);
      setDataSource(source);

      if (source === 'dataverse') {
        try {
          const groups = await dataverseService.accessGroupService.getAll();
          const withMembers = await Promise.all(
            groups.map((group) => dataverseService.accessGroupService.getById(group.id))
          );
          const map = {};
          for (const group of withMembers) {
            for (const lock of group.locks) {
              if (!map[lock.id]) {
                map[lock.id] = [];
              }
              map[lock.id].push(group);
            }
          }
          setGroupsByLockId(map);
        } catch (groupError) {
          console.warn('Could not load access groups for locks:', groupError);
          setGroupsByLockId({});
        }
      }
    } catch (loadError) {
      console.error('Error loading locks:', loadError);
      if (handleDataverseLoadError(loadError, navigate)) {
        return;
      }
      setLocks([]);
      setDataSource('dataverse');
      setError(getDataverseErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadLocks();
  }, [loadLocks]);

  const handleNoteSaved = useCallback((lockId, note) => {
    setLocks((prev) =>
      prev.map((entry) => (entry.lockId === lockId || entry.id === lockId ? { ...entry, note } : entry))
    );
    setDetailsLock((prev) =>
      prev && (prev.lockId === lockId || prev.id === lockId) ? { ...prev, note } : prev
    );
  }, []);

  const filteredLocks = locks.filter((lock) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const searchFields = [
      lock.name,
      lock.lockId,
      lock.location,
      lock.city,
      lock.vendorType,
      lock.externalId,
      lock.organizationName,
      lock.note,
      lock.duplicateSourceLock?.name,
      lock.duplicateSourceLock?.id,
      lock.duplicateSourceLock?.project?.id,
      lock.duplicateSourceLock?.project?.name,
      lock.duplicateSourceLock?.project?.organizationId,
      lock.duplicateSourceLock?.project?.organizationName
    ]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());
    const matchesSearch =
      !normalizedSearch || searchFields.some((value) => value.includes(normalizedSearch));
    const matchesType = !filterType || lock.provider === filterType;
    const inactiveLocation = isInactiveLocation(lock);
    const matchesStatus =
      filterStatus === 'inactive'
        ? inactiveLocation
        : !inactiveLocation &&
          (!filterStatus ||
            (filterStatus === 'available' && lock.assignedResidents.length === 0) ||
            (filterStatus === 'assigned' && lock.assignedResidents.length > 0));
    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    total: locks.length,
    available: locks.filter((l) => l.assignedResidents.length === 0).length,
    assigned: locks.filter((l) => l.assignedResidents.length > 0).length,
    byProvider: listProvidersForFilter().reduce((acc, provider) => {
      acc[provider.id] = countLocksByProvider(locks, provider.id);
      return acc;
    }, {})
  };

  const handleCreateMoveOrder = (lock) => {
    navigate('/orders', {
      state: {
        orderPreset: {
          type: ORDER_TYPES.MOVE,
          lockId: lock.lockId,
          description: `Flytting av lås ${lock.name}`
        }
      }
    });
  };

  const handleCreateServiceOrder = (lock) => {
    navigate('/orders', {
      state: {
        orderPreset: {
          type: ORDER_TYPES.SERVICE,
          lockId: lock.lockId,
          description: `Serviceoppdrag for ${lock.name}`
        }
      }
    });
  };

  const handleManageKeys = (lock) => {
    setKeyDialogLock(lock);
    setKeyDialogOpen(true);
  };

  const handleCloseKeyDialog = () => {
    setKeyDialogOpen(false);
    setKeyDialogLock(null);
  };

  const handleSyncFromUnloc = async () => {
    if (dataSource === 'demo' || syncing) {
      return;
    }

    setSyncing(true);
    setSyncError(null);
    setSyncMessage('Henter låser fra Unloc…');

    try {
      const result = await dataverseService.lockSyncService.syncFromUnloc({
        onProgress: (p) => {
          if (p?.message) {
            setSyncMessage(p.message);
          }
        }
      });
      const parts = [`${result.locksCount} fra Unloc`];
      if (result.upserted) parts.push(`${result.upserted} skrevet`);
      if (result.skipped) parts.push(`${result.skipped} uendret (hoppet over)`);
      if (result.deactivated) parts.push(`${result.deactivated} deaktivert`);
      if (result.enrolled) parts.push(`${result.enrolled} lagt i tilgangsgruppe`);
      if (result.failed) parts.push(`${result.failed} feilet`);
      setSyncMessage(`Synk ferdig (${parts.join(', ')})`);
      if (result.failed > 0 && result.errors?.length) {
        console.warn('Lock sync partial failures:', result.errors);
        setSyncError(
          `${result.failed} lås(er) feilet:\n${result.errors.slice(0, 8).join('\n')}`
        );
      }
      await loadLocks();
    } catch (err) {
      console.error('Lock sync failed:', err);
      setSyncError(getDataverseErrorMessage(err) || err.message || 'Lås-synk feilet.');
      setSyncMessage(null);
    } finally {
      setSyncing(false);
    }
  };

  const sharedLockActions = {
    onCreateMoveOrder: handleCreateMoveOrder,
    onCreateServiceOrder: handleCreateServiceOrder,
    onManageKeys: handleManageKeys,
    onAddToGroup: setAddGroupLock,
    onOpenDetails: setDetailsLock,
    onViewLog: (lock) =>
      navigate(
        `/reports?report=hendelseslogg&lockId=${encodeURIComponent(
          lock.externalId || lock.lockId
        )}`
      )
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="600">
            Låser
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Oversikt over alle låser i systemet
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <Sync />}
            onClick={handleSyncFromUnloc}
            disabled={syncing || dataSource === 'demo' || loading}
          >
            {syncing ? 'Oppdaterer…' : 'Oppdater fra Unloc'}
          </Button>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, next) => {
              if (next) setViewMode(next);
            }}
          >
            <ToggleButton value="list">
              <ViewList sx={{ mr: 0.75 }} />
              Liste
            </ToggleButton>
            <ToggleButton value="tiles">
              <ViewModule sx={{ mr: 0.75 }} />
              Kort
            </ToggleButton>
            <ToggleButton value="map">
              <MapIcon sx={{ mr: 0.75 }} />
              Kart
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {dataSource === 'demo' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {DEMO_DATA_BANNER_TEXT}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {syncMessage && (
        <Alert
          severity={syncing ? 'info' : syncError ? 'warning' : 'success'}
          sx={{ mb: 3, whiteSpace: 'pre-line' }}
          onClose={() => !syncing && setSyncMessage(null)}
        >
          {syncMessage}
        </Alert>
      )}

      {syncError && (
        <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-line' }} onClose={() => setSyncError(null)}>
          {syncError}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="primary">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Totalt
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="success.main">
                {stats.available}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tilgjengelige
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="warning.main">
                {stats.assigned}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tilordnet
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="info.main">
                {stats.byProvider.unloc || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unloc
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="secondary.main">
                {stats.total - (stats.byProvider.unloc || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Andre
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Søk etter navn, ID, lokasjon eller organisasjon..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Leverandør"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                variant="outlined"
                SelectProps={{
                  native: true,
                  displayEmpty: false
                }}
                InputLabelProps={{
                  shrink: true
                }}
              >
                <option value="">Alle leverandører</option>
                {listProvidersForFilter().map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                variant="outlined"
                SelectProps={{
                  native: true,
                  displayEmpty: false
                }}
                InputLabelProps={{
                  shrink: true
                }}
              >
                <option value="">Alle aktive</option>
                <option value="available">Tilgjengelig</option>
                <option value="assigned">Tilordnet</option>
                <option value="inactive">Inaktiv</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`${filteredLocks.length} av ${locks.length}`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {viewMode === 'map' ? (
        <Card>
          <CardContent>
            <LocksMap locks={filteredLocks} center={mapCenter} />
          </CardContent>
        </Card>
      ) : viewMode === 'tiles' ? (
        <>
          <Grid container spacing={3}>
            {filteredLocks.map((lock) => (
              <Grid item xs={12} sm={6} md={4} key={lock.id}>
                <LockCard
                  lock={lock}
                  accessGroups={groupsByLockId[lock.lockId] || []}
                  {...sharedLockActions}
                />
              </Grid>
            ))}
          </Grid>

          {filteredLocks.length === 0 && !loading && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  Ingen låser funnet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Prøv å justere søkekriteriene
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <>
          <Stack spacing={1}>
            {filteredLocks.map((lock) => (
              <LockRow
                key={lock.id}
                lock={lock}
                accessGroups={groupsByLockId[lock.lockId] || []}
                {...sharedLockActions}
              />
            ))}
          </Stack>

          {filteredLocks.length === 0 && !loading && (
            <Card sx={{ mt: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  Ingen låser funnet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Prøv å justere søkekriteriene
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <LockDetailsDialog
        open={Boolean(detailsLock)}
        lock={detailsLock}
        onClose={() => setDetailsLock(null)}
        onNoteSaved={handleNoteSaved}
        onViewLog={(lock) =>
          navigate(
        `/reports?report=hendelseslogg&lockId=${encodeURIComponent(
          lock.externalId || lock.lockId
        )}`
      )
        }
      />

      {keyDialogLock?.provider === 'unloc' ? (
        <LockKeysDialog
          open={keyDialogOpen}
          lock={keyDialogLock}
          onClose={handleCloseKeyDialog}
        />
      ) : (
        <KeyRequestDialog
          open={keyDialogOpen}
          onClose={handleCloseKeyDialog}
          scope={KEY_REQUEST_SCOPE.SINGLE_LOCK}
          lock={keyDialogLock}
          onSuccess={handleCloseKeyDialog}
        />
      )}

      <AddToAccessGroupDialog
        open={Boolean(addGroupLock)}
        onClose={() => setAddGroupLock(null)}
        title={`Legg ${addGroupLock?.name || 'lås'} til i gruppe`}
        addToGroup={(groupId) =>
          dataverseService.accessGroupService.addLockToGroup(groupId, addGroupLock.lockId)
        }
        onAdded={loadLocks}
      />
    </Box>
  );
};

export default LocksPage;
